'use client';

import React, { useEffect, useRef } from 'react';
import { useGenerateTalkTrackMutation } from 'src/api/redux/profileConfiguration';
import { useGetCompanySettingsQuery } from 'src/api/redux';
import { COMPANY_SETTINGS_IDS } from 'src/api/redux/settings/constants';
import { useProfile } from 'src/context/providers/ProfileProvider';

interface TalkTrackProps {
  talkTrack?: string | null;
  playId: number;
  label: string;
}

const TalkTrack: React.FC<TalkTrackProps> = ({ talkTrack, playId, label }) => {
  const { data: companySettings } = useGetCompanySettingsQuery(undefined);
  const isEnabled =
    companySettings?.results?.find(
      (s) => s.code === COMPANY_SETTINGS_IDS.PLAY_SUMMARIES_ENABLED
    )?.value !== 'false';

  const { entityDetails } = useProfile();
  const providerId = entityDetails?.provider_id;
  const entityId = providerId ?? entityDetails?.id;

  const [generateTalkTrack, { isLoading: isGenerating, isError }] =
    useGenerateTalkTrackMutation();

  const hasTriggered = useRef(false);

  useEffect(() => {
    if (talkTrack != null || hasTriggered.current) return;
    if (!providerId || !entityId) return;

    hasTriggered.current = true;
    generateTalkTrack({ providerId, playId, entityId });
  }, [talkTrack, providerId, entityId, playId, generateTalkTrack]);

  if (!isEnabled) return null;

  return (
    <div
      className="bg-sky-50 rounded-xl p-5 flex flex-col gap-3 border border-neutral-200"
      data-testid="play-summary"
      role="region"
      aria-label={`Play summary for ${label}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-sm font-bold text-primary">Play Summary</span>
      </div>
      {talkTrack != null ? (
        <p
          className="text-sm font-medium text-primary leading-relaxed"
          data-testid="talk-track-content"
        >
          {talkTrack}
        </p>
      ) : isError ? (
        <p
          className="text-xs font-medium text-red-600"
          data-testid="talk-track-error"
        >
          Unable to generate play summary.
        </p>
      ) : isGenerating ? (
        <div
          className="flex flex-col gap-2 animate-pulse"
          data-testid="talk-track-loading"
          aria-label="Generating play summary"
        >
          <div className="h-3 w-full bg-sky-100 rounded" />
          <div className="h-3 w-full bg-sky-100 rounded" />
          <div className="h-3 w-3/4 bg-sky-100 rounded" />
        </div>
      ) : null}
    </div>
  );
};

export default TalkTrack;
